/**
 * Data layer service for Google Analytics tracking and event management.
 * Handles page view tracking and form interaction events.
 */

import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

/**
 * Global window interface extension to include the dataLayer property.
 *
 * @remarks This interface extends the global Window object to include
 * Google Analytics dataLayer functionality. This extension is necessary
 * for TypeScript to recognize the dataLayer property on the window object.
 */
declare global {
  /**
   * Extended Window interface with Google Analytics dataLayer support.
   *
   * @remarks Augments the standard Window interface to include the dataLayer
   * property required for Google Analytics tracking. This allows type-safe
   * access to the Google Analytics data layer throughout the application.
   */
  interface Window {
    /**
     * Google Analytics data layer array for tracking events.
     *
     * @remarks Array that stores tracking events and configuration data
     * for Google Analytics. Events pushed to this array are automatically
     * processed by the Google Analytics tracking code.
     */
    dataLayer: any[];
  }
}

/**
 * Service for managing Google Analytics data layer events and page tracking.
 *
 * @remarks Provides integration with Google Analytics through the data layer.
 * Automatically tracks page views on route changes and provides methods for
 * tracking specific user interactions like form submissions.
 *
 */
@Injectable({
  providedIn: 'root',
})
export class DataLayerService {
  /** @private Angular router service for navigation event tracking */
  private router = inject(Router);

  /**
   * Initialize dataLayer and set up router event listeners.
   *
   * @remarks Initializes the Google Analytics data layer if it doesn't exist
   * and sets up automatic page view tracking for route navigation events.
   * Should be called once during application initialization.
   *
   */
  initDataLayer(): void {
    // Initialize dataLayer if it doesn't exist
    window.dataLayer = window.dataLayer || [];

    // Listen for route changes
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.pushPageViewEvent(event.urlAfterRedirects);
    });
  }

  /**
   * Push a page view event to the dataLayer.
   *
   * @remarks Creates and pushes a standardized page view event to Google Analytics
   * with content grouping and page type classification. Automatically determines
   * content categories and page types based on the URL structure.
   *
   * @param {string} currentPage - The current page URL path
   */
  private pushPageViewEvent(currentPage: string): void {
    const contentGroup2 = this.getContentGroup2(currentPage);
    const pageType = currentPage === '/' ? 'startseite' : 'content';

    window.dataLayer.push({
      event: 'page-data',
      currentPage: currentPage,
      contentGroup1: 'SYM',
      contentGroup2: contentGroup2,
      pageType: pageType,
      contentId: `SYM.${contentGroup2}.${pageType}`,
    });
  }

  /**
   * Get contentGroup2 value based on the current page.
   *
   * @remarks Determines the content group classification for analytics
   * based on the URL path. Uses the first URL segment as the content group
   * or defaults to 'home' for the root path.
   *
   * @param {string} currentPage - The current page URL path
   * @returns {string} The content group identifier for analytics
   */
  private getContentGroup2(currentPage: string): string {
    if (currentPage === '/') {
      return 'home';
    }

    // Extract first segment of the URL
    const path = currentPage.split('/').filter(segment => segment.length > 0);
    return path.length > 0 ? path[0] : 'home';
  }

  /**
   * Push form success event to dataLayer.
   *
   * @remarks Tracks successful form submissions for analytics.
   * Used to measure form completion rates and user engagement
   * with different contact methods.
   *
   * @param {('telefonisch' | 'schriftlich')} contactType - The type of contact requested (phone or written)
   */
  pushFormSuccessEvent(contactType: 'telefonisch' | 'schriftlich'): void {
    window.dataLayer.push({
      event: 'form-success',
      eventCategory: 'Form',
      BTNeventAction: `kontakt_beratung-vereinbaren_form_beratung-vereinbaren+${contactType}_formsuccess`,
    });
  }
}
